import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SharedService } from 'app/modules/common/services/shared-service';
import { FileExportService } from 'app/modules/selection-generic/services/file-export.service';
import { DespachoCoordinador, Persona } from './models/persona.model';
import { DespachoService } from './services/despacho-coordinadores.component.service';
import {
    DespachoCoordinadoresReportDialogResult,
    DespachoCoordinadoresReportPopupComponent
} from './popups/despacho-coordinadores-report-popup/despacho-coordinadores-report-popup.component';

@Component({
    selector: 'app-despacho-coordinadores',
    templateUrl: './despacho-coordinadores.component.html',
    styleUrls: ['./despacho-coordinadores.component.scss'],
    standalone: false
})
export class DespachoCoordinadoresComponent implements OnInit {
    year: number = new Date().getFullYear();
    months: string[] = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

    personas: Persona[] = [];
    selectedPersonaId: number | null = null;
    selectedDespachoId: number | null = null;

    loading: boolean = false;
    reportLoading: boolean = false;
    error: string | null = null;

    fechaReunion: string = '';
    comentarios: string = '';
    visitasRealizadas: string[] = [];

    constructor(
        private dialog: MatDialog,
        private despachoService: DespachoService,
        private sharedService: SharedService,
        private fileExportService: FileExportService,
    ) { }

    ngOnInit(): void {
        this.loadCoordinadores();
    }

    loadCoordinadores(): void {
        this.loading = true;
        this.error = null;
        this.clearSelection();

        this.despachoService.GetAllCoordinatorsList(this.year.toString()).subscribe(response => {
            const responseData = response as any;
            const code = responseData?.code ?? responseData?.Code;
            const result = responseData?.result ?? responseData?.Result;

            if (response && code === 0 && Array.isArray(result)) {
                this.personas = this.transformarDatosCoordinadores(result);
            } else {
                this.personas = [];
                this.error = 'No se pudieron cargar los despachos de coordinadores';
            }

            this.loading = false;
        }, err => {
            console.error(err);
            this.personas = [];
            this.error = 'Error al cargar despachos de coordinadores';
            this.loading = false;
        });
    }

    transformarDatosCoordinadores(coordinadores: any[]): Persona[] {
        const coordinadoresPorNombre = new Map<string, Persona>();

        coordinadores.forEach((coord, index) => {
            const nombreCoordinador = coord.coordinador ?? coord.Coordinador;

            if (!nombreCoordinador) {
                return;
            }

            const id = Number(coord.id ?? coord.Id ?? index + 1);
            const mes = Number(coord.mes ?? coord.Mes);
            const anyo = Number(coord.anyo ?? coord.Anyo ?? this.year);
            const fechaVisita = this.toStringValue(coord.fechaVisita ?? coord.FechaVisita);
            const comentarios = this.toStringValue(coord.comentarios ?? coord.Comentarios);
            const realizada = (coord.estaRealizada ?? coord.EstaRealizada) === 1;

            if (!mes || mes < 1 || mes > 12) {
                return;
            }

            const despacho: DespachoCoordinador = {
                id,
                fechaVisita,
                fechaCompleta: this.formatFechaCompleta(fechaVisita, anyo),
                comentarios,
                mes,
                anyo,
                realizada
            };

            if (!coordinadoresPorNombre.has(nombreCoordinador)) {
                coordinadoresPorNombre.set(nombreCoordinador, {
                    id,
                    nombre: nombreCoordinador,
                    visitas: {},
                    visitasRealizadas: [],
                    despachos: [],
                    fechaReunion: '',
                    comentarios: ''
                });
            }

            const persona = coordinadoresPorNombre.get(nombreCoordinador);
            if (!persona) {
                return;
            }

            const mesAbreviado = this.months[mes - 1];
            if (!persona.visitas[mesAbreviado]) {
                persona.visitas[mesAbreviado] = [];
            }

            persona.despachos.push(despacho);
            persona.visitas[mesAbreviado].push(despacho);

            if (realizada && fechaVisita) {
                persona.visitasRealizadas.push(fechaVisita);
            }
        });

        const personas = Array.from(coordinadoresPorNombre.values());

        personas.forEach(persona => {
            persona.despachos.sort((a, b) => this.getDespachoTime(b) - this.getDespachoTime(a));

            this.months.forEach(month => {
                if (persona.visitas[month]) {
                    persona.visitas[month].sort((a, b) => this.getDespachoTime(a) - this.getDespachoTime(b));
                }
            });

            const latestDespacho = persona.despachos[0];
            if (latestDespacho) {
                persona.fechaReunion = latestDespacho.fechaCompleta;
                persona.comentarios = latestDespacho.comentarios;
            }
        });

        return personas.sort((a, b) => a.nombre.localeCompare(b.nombre));
    }

    changeYear(increment: number): void {
        this.year += increment;
        this.loadCoordinadores();
    }

    selectPersona(persona: Persona): void {
        if (this.selectedPersonaId === persona.id && !this.selectedDespachoId) {
            this.clearSelection();
            return;
        }

        this.selectedPersonaId = persona.id;
        const latestDespacho = persona.despachos[0];

        if (latestDespacho) {
            this.applyDespachoSelection(persona, latestDespacho);
        } else {
            this.selectedDespachoId = null;
            this.fechaReunion = '';
            this.comentarios = '';
            this.visitasRealizadas = [];
        }
    }

    selectDespacho(persona: Persona, despacho: DespachoCoordinador, event: MouseEvent): void {
        event.stopPropagation();
        this.applyDespachoSelection(persona, despacho);
    }

    crear(): void {
        this.error = 'Crear despacho de coordinador queda pendiente de conectar';
    }

    eliminar(): void {
        if (!this.selectedDespachoId) {
            this.error = 'Debe seleccionar un registro para eliminar';
            return;
        }

        this.error = 'Eliminar despacho de coordinador queda pendiente de conectar';
    }

    editar(): void {
        if (!this.selectedDespachoId) {
            this.error = 'Debe seleccionar un registro para editar';
            return;
        }

        this.error = 'Editar despacho de coordinador queda pendiente de conectar';
    }

    guardar(): void {
        if (!this.selectedDespachoId) {
            this.error = 'Debe seleccionar un registro para guardar cambios';
            return;
        }

        this.error = 'Guardar despacho de coordinador queda pendiente de conectar';
    }

    deshacer(): void {
        this.loadCoordinadores();
    }

    generarInformeWord(): void {
        if (!this.selectedDespachoId) {
            this.sharedService.ShowSnackBar('Debe seleccionar un registro para generar el Word');
            return;
        }

        this.reportLoading = true;
        this.error = null;

        this.despachoService.GetCoordinatorVisitWordReport(this.selectedDespachoId.toString()).subscribe(res => {
            this.reportLoading = false;

            if (res && res.code === 0 && res.result && res.result.file && res.result.fileName) {
                this.fileExportService.DownloadFile(res.result.file, res.result.fileName);
                return;
            }

            this.sharedService.ShowSnackBar('No se pudo generar el informe Word');
        }, err => {
            console.error(err);
            this.reportLoading = false;
            this.sharedService.ShowSnackBar('No se pudo generar el informe Word');
            this.error = 'No se pudo generar el informe Word';
        });
    }

    abrirInformeRango(): void {
        const persona = this.getSelectedPersona();
        if (!persona) {
            this.sharedService.ShowSnackBar('Debe seleccionar un coordinador para generar el informe');
            return;
        }

        const dialogRef = this.dialog.open(DespachoCoordinadoresReportPopupComponent, {
            width: '460px',
            autoFocus: false,
            data: {
                coordinador: persona.nombre,
                year: this.year
            }
        });

        dialogRef.afterClosed().subscribe((result: DespachoCoordinadoresReportDialogResult | undefined) => {
            if (!result) {
                return;
            }

            this.generarInformeWordRango(persona.nombre, result);
        });
    }

    generarInformeWordRango(coordinador: string, result: DespachoCoordinadoresReportDialogResult): void {
        this.reportLoading = true;
        this.error = null;

        this.despachoService.GetCoordinatorVisitWordReportByRange({
            coordinador,
            dateFrom: result.dateFrom,
            dateTo: result.dateTo,
        }).subscribe(res => {
            this.reportLoading = false;

            if (res && res.code === 0 && res.result && res.result.file && res.result.fileName) {
                this.fileExportService.DownloadFile(res.result.file, res.result.fileName);
                return;
            }

            this.sharedService.ShowSnackBar('No hay datos para generar el informe');
        }, err => {
            console.error(err);
            this.reportLoading = false;
            this.sharedService.ShowSnackBar('No se pudo generar el informe');
            this.error = 'No se pudo generar el informe';
        });
    }

    updateFechaReunion(): void {
        const despacho = this.getSelectedDespacho();
        if (despacho) {
            despacho.fechaCompleta = this.fechaReunion;
        }
    }

    updateComentarios(): void {
        const despacho = this.getSelectedDespacho();
        if (despacho) {
            despacho.comentarios = this.comentarios;
        }
    }

    closeError(): void {
        this.error = null;
    }

    private getSelectedDespacho(): DespachoCoordinador | undefined {
        if (!this.selectedDespachoId) {
            return undefined;
        }

        for (const persona of this.personas) {
            const despacho = persona.despachos.find(item => item.id === this.selectedDespachoId);
            if (despacho) {
                return despacho;
            }
        }

        return undefined;
    }

    private getSelectedPersona(): Persona | undefined {
        if (!this.selectedPersonaId) {
            return undefined;
        }

        return this.personas.find(persona => persona.id === this.selectedPersonaId);
    }

    private applyDespachoSelection(persona: Persona, despacho: DespachoCoordinador): void {
        this.selectedPersonaId = persona.id;
        this.selectedDespachoId = despacho.id;
        this.fechaReunion = despacho.fechaCompleta;
        this.comentarios = despacho.comentarios;
        this.visitasRealizadas = persona.visitasRealizadas || [];
    }

    private clearSelection(): void {
        this.selectedPersonaId = null;
        this.selectedDespachoId = null;
        this.fechaReunion = '';
        this.comentarios = '';
        this.visitasRealizadas = [];
    }

    private formatFechaCompleta(fechaVisita: string, anyo: number): string {
        if (!fechaVisita) {
            return '';
        }

        if (fechaVisita.includes('/')) {
            const parts = fechaVisita.split('/');
            if (parts.length === 2) {
                return `${parts[0]}/${parts[1]}/${anyo}`;
            }
        }

        return fechaVisita;
    }

    private getDespachoTime(despacho: DespachoCoordinador): number {
        const parts = despacho.fechaCompleta.split('/');
        if (parts.length !== 3) {
            return 0;
        }

        const day = Number(parts[0]);
        const month = Number(parts[1]);
        const year = Number(parts[2]);

        if ([day, month, year].some(value => Number.isNaN(value))) {
            return 0;
        }

        return new Date(year, month - 1, day).getTime();
    }

    private toStringValue(value: unknown): string {
        return value === null || value === undefined ? '' : String(value);
    }
}
